import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, MapPin, DollarSign, Navigation, Clock, Shield, Smartphone, History, TrendingUp } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const HERO_IMG = 'https://images.unsplash.com/photo-1765820572540-cbc7a9ca8ee7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHx0cnVjayUyMGRyaXZlciUyMGNhYiUyMGhpZ2h3YXl8ZW58MHx8fHwxNzczMjY3NzkyfDA&ixlib=rb-4.1.0&q=85&w=1600';

const painPoints = [
  { problem: 'Wasted hours searching for loads', solution: 'Loads come to you. Filter by distance, pay, and cargo type.' },
  { problem: 'Unclear pay and hidden fees', solution: 'Transparent per-mile rates. See your exact earnings before accepting.' },
  { problem: 'Paper-based logs and receipts', solution: 'Digital everything. Scan receipts with AI, track hours automatically.' },
  { problem: 'No GPS, no directions', solution: 'Built-in full-screen GPS navigation with turn-by-turn guidance.' },
];

const features = [
  { icon: Smartphone, title: 'One-Tap Accept', desc: 'See a load you like? Accept it instantly. No calls, no emails, no waiting.' },
  { icon: Navigation, title: 'GPS Navigation', desc: 'Full-screen turn-by-turn directions. Never leave the app to find your way.' },
  { icon: DollarSign, title: 'Earnings Dashboard', desc: 'Track every dollar. See your rate per mile, total earnings, and payment history.' },
  { icon: History, title: 'Load History', desc: 'Every load you have hauled, organized and searchable. Tax season made easy.' },
  { icon: Shield, title: 'Fair & Transparent', desc: 'No hidden fees. The rate you see is the rate you get. Period.' },
  { icon: TrendingUp, title: 'Grow Your Income', desc: 'Smart load suggestions based on your location and preferences.' },
];

const stats = [
  { value: '$2.50+', label: 'Average rate per mile' },
  { value: '< 30s', label: 'To accept a load' },
  { value: '24/7', label: 'Load availability' },
];

export default function DriversLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090B] text-white" data-testid="drivers-landing">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 flex items-center justify-between px-6 md:px-12 py-5 z-20 bg-[#09090B]/70 backdrop-blur-md" data-testid="drivers-nav">
        <div className="flex items-center gap-3">
          <BrandLogo size="default" darkBg={true} />
          <span className="text-xs text-zinc-500 border-l border-zinc-700 pl-3 hidden sm:block">For Drivers</span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate('/login')} data-testid="driver-landing-login">
            Log In
          </Button>
          <Button className="bg-[#E5A100] hover:bg-yellow-500 text-black font-semibold rounded-full px-6"
            onClick={() => navigate('/register')} data-testid="driver-landing-register">
            Start Driving <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-24 md:pt-32 pb-32">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Driver on highway" className="w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-[#E5A100]/10 border border-[#E5A100]/20 rounded-full px-4 py-1.5 mb-6">
            <div className="w-2 h-2 rounded-full bg-[#E5A100] animate-pulse" />
            <span className="text-[#E5A100] text-xs font-medium tracking-wide">BUILT FOR TRUCKERS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Your road.<br />
            Your rules.<br />
            <span className="bg-gradient-to-r from-[#E5A100] via-white to-[#E5A100] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Your earnings.
            </span>
          </h1>
          <p className="text-zinc-400 text-lg md:text-xl max-w-xl mb-10 leading-relaxed">
            Stop chasing loads. Let them come to you. Accept with one tap,
            navigate with built-in GPS, and get paid what you deserve.
          </p>
          <Button className="bg-[#E5A100] hover:bg-yellow-500 text-black font-semibold rounded-full px-8 py-6 text-lg"
            onClick={() => navigate('/register')} data-testid="driver-hero-cta">
            Start Driving Free <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-zinc-800 px-6 md:px-12 py-8">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-[#E5A100]">{s.value}</p>
              <p className="text-zinc-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pain Points */}
      <section className="px-6 md:px-12 py-20" data-testid="driver-pain-points">
        <h2 className="text-base md:text-lg font-bold text-center mb-12">
          We know the <span className="text-[#E5A100]">pain</span>. We built the <span className="text-white">fix</span>.
        </h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {painPoints.map((p, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-zinc-800 rounded-2xl p-5 hover:border-zinc-700 transition-colors">
              <div>
                <p className="text-xs text-red-400 font-medium tracking-wider mb-1">THE PROBLEM</p>
                <p className="text-zinc-300 text-sm">{p.problem}</p>
              </div>
              <div>
                <p className="text-xs text-[#E5A100] font-medium tracking-wider mb-1">OUR SOLUTION</p>
                <p className="text-zinc-300 text-sm">{p.solution}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 md:px-12 pb-20" data-testid="driver-features">
        <h2 className="text-base md:text-lg font-bold text-center mb-12">
          Everything you need. <span className="text-white">Nothing you don't.</span>
        </h2>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="border border-zinc-800 bg-zinc-900/40 rounded-2xl p-6 hover:border-[#E5A100]/30 transition-all duration-300 group">
              <div className="w-10 h-10 rounded-xl bg-[#E5A100]/10 flex items-center justify-center mb-4 group-hover:bg-[#E5A100]/20 transition-colors">
                <f.icon className="w-5 h-5 text-[#E5A100]" strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-3xl p-10 md:p-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to hit the road?</h2>
          <p className="text-zinc-400 mb-8 max-w-md mx-auto">
            Join thousands of drivers already using TORQ.
            Sign up in under 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-[#E5A100] hover:bg-yellow-500 text-black font-semibold rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/register')} data-testid="driver-bottom-cta">
              Create Driver Account <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="outline" className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')}>
              Already have an account?
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 px-6 md:px-12 py-6 flex items-center justify-between">
        <BrandLogo size="sm" darkBg={true} className="opacity-50" />
        <div className="flex items-center gap-6 text-xs text-zinc-600">
          <button onClick={() => navigate('/')} className="hover:text-zinc-400 transition-colors">Home</button>
          <button onClick={() => navigate('/for-shippers')} className="hover:text-zinc-400 transition-colors">For Shippers</button>
          <button onClick={() => navigate('/for-fleets')} className="hover:text-zinc-400 transition-colors">For Fleets</button>
        </div>
      </footer>
    </div>
  );
}
